/**
 * Risk Contributors Table - Top 10 positions by risk contribution
 * Uses heuristic calculation: weight × asset class volatility × currency penalty × concentration penalty
 * PRIMARY VALUE: AM identification of concentrated risk sources
 */

import React, { useMemo, useState } from 'react';
import { ArrowUpDown, AlertTriangle, Download } from 'lucide-react';

interface Position {
  isin: string;
  security_name: string;
  market_value: number;
  asset_class: string;
  currency: string;
}

interface RiskContributorsTableProps {
  positions: Position[];
  totalValue: number;
  currency?: string;
}

interface RiskContributor {
  isin: string;
  name: string;
  value: number;
  weight: number;
  assetClass: string;
  currency: string;
  riskPoints: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}

type SortField = 'riskPoints' | 'weight' | 'value';

/** Heuristic volatility multipliers by asset class — used for estimated risk scoring */
const VOLATILITY_BY_CLASS: Record<string, number> = {
  'Equity': 2.0,
  'Stock': 2.0,
  'Fund': 1.8,
  'ETF': 1.8,
  'Bond': 0.5,
  'Fixed Income': 0.5,
  'Cash': 0.1,
  'Money Market': 0.1,
  'Real Estate': 2.5,
  'Alternative': 2.5,
  'Derivative': 3.0,
  'Option': 3.0,
};

const RiskContributorsTable: React.FC<RiskContributorsTableProps> = ({
  positions,
  totalValue,
  currency = 'CHF'
}) => {
  const [sortField, setSortField] = useState<SortField>('riskPoints');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Calculate risk contributors with heuristic formula
  const riskContributors: RiskContributor[] = useMemo(() => {
    console.log('RiskContributorsTable - positions:', positions?.length, 'totalValue:', totalValue);

    if (totalValue === 0 || !positions || positions.length === 0) return [];

    const contributors = positions.map(p => {
      const weight = (p.market_value / totalValue) * 100;

      // Get volatility multiplier for asset class
      const volMultiplier = VOLATILITY_BY_CLASS[p.asset_class] || 1.0;

      // Currency risk: add penalty for non-base currency
      const currencyMultiplier = p.currency !== currency ? 1.2 : 1.0;

      // Concentration penalty: positions > 10% get extra risk
      const concentrationPenalty = weight > 10 ? 1.3 : weight > 5 ? 1.1 : 1.0;

      // Calculate risk points
      const riskPoints = weight * volMultiplier * currencyMultiplier * concentrationPenalty;

      // Determine risk level
      let riskLevel: 'low' | 'medium' | 'high' | 'critical';
      if (riskPoints > 30) riskLevel = 'critical';
      else if (riskPoints > 20) riskLevel = 'high';
      else if (riskPoints > 10) riskLevel = 'medium';
      else riskLevel = 'low';

      return {
        isin: p.isin,
        name: p.security_name,
        value: p.market_value,
        weight,
        assetClass: p.asset_class,
        currency: p.currency,
        riskPoints,
        riskLevel
      };
    });

    // Sort by risk points descending and take top 10
    return contributors
      .sort((a, b) => b.riskPoints - a.riskPoints)
      .slice(0, 10);
  }, [positions, totalValue, currency]);

  // Apply sorting
  const sortedContributors = useMemo(() => {
    const sorted = [...riskContributors];
    sorted.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      return sortDirection === 'asc'
        ? (aVal > bVal ? 1 : -1)
        : (aVal > bVal ? -1 : 1);
    });
    return sorted;
  }, [riskContributors, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRiskLevelText = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  const handleExport = () => {
    // CSV export for top risk contributors
    const headers = ['Position', 'Value', 'Weight', 'Asset Class', 'Currency', 'Risk Points', 'Risk Level'];
    const rows = sortedContributors.map(c => [
      c.name,
      c.value.toFixed(2),
      c.weight.toFixed(2) + '%',
      c.assetClass,
      c.currency,
      c.riskPoints.toFixed(1),
      getRiskLevelText(c.riskLevel)
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk-contributors.csv';
    a.click();
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Top 10 Risk Contributors</h3>
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-xs font-medium">
            Estimated
          </span>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4">
        Calculated using position weight, asset class volatility, currency exposure, and concentration penalties.
      </p>

      {/* Empty State or Table */}
      {sortedContributors.length === 0 ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium mb-1">No positions available</p>
          <p className="text-sm text-gray-500">
            Position data will appear here when available
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Position
                  </th>
              <th
                className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('value')}
              >
                <div className="flex items-center justify-end gap-1">
                  Value
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('weight')}
              >
                <div className="flex items-center justify-end gap-1">
                  Weight
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Asset Class
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Currency
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('riskPoints')}
              >
                <div className="flex items-center justify-end gap-1">
                  Risk Score
                  <ArrowUpDown className="w-3 h-3" />
                </div>
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Risk Level
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedContributors.map((contributor) => (
              <tr key={contributor.isin} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium text-gray-900">{contributor.name}</div>
                  <div className="text-xs text-gray-500">{contributor.isin}</div>
                </td>
                <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                  {formatCurrency(contributor.value)}
                </td>
                <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                  {contributor.weight.toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {contributor.assetClass}
                </td>
                <td className="px-4 py-3 text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    contributor.currency !== currency
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {contributor.currency}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className="font-bold text-gray-900">
                    {contributor.riskPoints.toFixed(1)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${
                    getRiskLevelColor(contributor.riskLevel)
                  }`}>
                    {getRiskLevelText(contributor.riskLevel)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Top 10 positions account for {sortedContributors.reduce((sum, c) => sum + c.weight, 0).toFixed(1)}% of portfolio value
          </span>
          <span className="text-gray-600">
            Combined risk score: <span className="font-bold text-gray-900">
              {sortedContributors.reduce((sum, c) => sum + c.riskPoints, 0).toFixed(1)}
            </span>
          </span>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default RiskContributorsTable;
